import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Home, FolderKanban, BookOpen, ShieldAlert, AlertOctagon, FileBarChart, TrendingUp,
  GitPullRequest, GraduationCap, Gavel, HelpCircle, GitBranch, Briefcase, Layers, Flag, Stamp, ShieldCheck,
  LifeBuoy, Radar, Building2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { useEntitlements } from "@/context/EntitlementsContext";

const SECTIONS = [
  {
    name: null,
    items: [
      { label: "Home", icon: Home, path: "/dashboard", id: "nav-home" },
      { label: "All Projects", icon: FolderKanban, path: "/projects", id: "nav-all-projects" },
    ],
  },
  {
    name: "Hierarchy",
    items: [
      { label: "Portfolios", icon: Briefcase, path: "/portfolios", id: "nav-portfolios" },
      { label: "Programmes", icon: Layers, path: "/programmes", id: "nav-programmes" },
      { label: "Clients", icon: Building2, path: "/clients", id: "nav-clients", feature: "client_reporting" },
    ],
  },
  {
    name: "Project Controls",
    items: [
      { label: "Risks", icon: ShieldAlert, path: "/controls/risks", id: "nav-risks", module: "risks" },
      { label: "Issues", icon: AlertOctagon, path: "/controls/issues", id: "nav-issues", module: "issues" },
      { label: "Status Reports", icon: FileBarChart, path: "/controls/status-reports", id: "nav-status-reports" },
      { label: "Benefits", icon: TrendingUp, path: "/controls/benefits", id: "nav-benefits", module: "benefits", feature: "dependencies_benefits" },
      { label: "Change Requests", icon: GitPullRequest, path: "/controls/change-requests", id: "nav-change-requests", module: "change_requests", feature: "full_raid" },
      { label: "Lessons Learned", icon: GraduationCap, path: "/controls/lessons", id: "nav-lessons", module: "lessons", feature: "full_raid" },
      { label: "Decisions", icon: Gavel, path: "/controls/decisions", id: "nav-decisions", module: "decisions", feature: "full_raid" },
      { label: "Assumptions", icon: HelpCircle, path: "/controls/assumptions", id: "nav-assumptions", module: "assumptions", feature: "full_raid" },
      { label: "Dependencies", icon: GitBranch, path: "/controls/dependencies", id: "nav-dependencies", module: "dependencies", feature: "dependencies_benefits" },
    ],
  },
  {
    name: "Quick Links",
    items: [
      { label: "Approvals", icon: Stamp, path: "/approvals", id: "nav-approvals", module: "approvals", feature: "approvals_stage_gates" },
      { label: "Reports", icon: FileBarChart, path: "/reports", id: "nav-reports", module: "reports" },
      { label: "Compliance", icon: ShieldCheck, path: "/compliance", id: "nav-compliance", module: "compliance", feature: "compliance_scorecards" },
      { label: "Resources", icon: BookOpen, path: "/resources", id: "nav-resources", module: "resources" },
      { label: "Support", icon: LifeBuoy, path: "/support", id: "nav-support" },
    ],
  },
];

export const SideNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, moduleOn } = useBranding();
  const { can } = useEntitlements();
  const { user } = useAuth();

  const sections = SECTIONS
    .map((s) => ({
      ...s,
      items: s.items.filter((it) => (!it.module || moduleOn(it.module)) && can(it.feature)),
    }))
    .filter((s) => s.items.length > 0)
    .concat(user?.platform_admin ? [{
      name: "Platform",
      items: [{ label: "Platform Console", icon: Radar, path: "/platform", id: "nav-platform" }],
    }] : []);

  return (
    <nav data-testid="side-nav" className="hidden w-56 shrink-0 border-r border-slate-200 bg-white lg:block">
      <div className="sticky top-[152px] max-h-[calc(100vh-152px)] overflow-y-auto py-4">
        {sections.map((s, si) => (
          <div key={s.name || `s${si}`} className="mb-4">
            {s.name && (
              <div className="px-4 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                {t(s.name)}
              </div>
            )}
            {s.items.map((it) => {
              const active = location.pathname === it.path;
              return (
                <button
                  key={it.id}
                  data-testid={it.id}
                  onClick={() => navigate(it.path)}
                  className={`flex w-full items-center gap-2.5 border-l-2 px-4 py-1.5 text-left text-[13px] transition-colors ${
                    active
                      ? "border-l-[color:var(--brand-primary)] bg-slate-100 font-semibold text-slate-900"
                      : "border-l-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <it.icon size={15} style={{ color: active ? "var(--brand-primary)" : undefined }} />
                  <span className="truncate">{t(it.label)}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </nav>
  );
};
